/**
 * The signature mark.
 *
 * One band per floor, filled from the ground up in proportion to the share of
 * units in distress, so a short red stack and a tall lightly-marked one read
 * differently at a glance — which a number in a column can't show you.
 *
 * Where the county gives no usable floor count it draws a single slab rather
 * than guessing.
 */

import { pct } from "../lib/format";

const WIDTH = 16;
const HEIGHT = 46;
const MAX_BANDS = 18;
const GAP = 1.2;

interface Props {
  floors: number | null;
  rate: number | null;
  unsafe: boolean;
  /** Row index, used to stagger the reveal down the table. */
  index?: number;
}

export function Elevation({ floors, rate, unsafe, index = 0 }: Props) {
  if (!floors || floors < 2) {
    return (
      <svg
        className="elev slab"
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="Floor count unknown"
      >
        <rect
          className={`band ${unsafe ? "hit" : ""}`}
          x="1"
          y={HEIGHT - 9}
          width={WIDTH - 2}
          height="7"
        />
        <line className="ground" x1="0" y1={HEIGHT - 1} x2={WIDTH} y2={HEIGHT - 1} />
      </svg>
    );
  }

  const bandCount = Math.min(floors, MAX_BANDS);
  const bandHeight = (HEIGHT - 4 - GAP * (bandCount - 1)) / bandCount;
  // Fill upward from the ground: at least one band is always lit, so a
  // building in the table never looks untouched.
  const litFrom = bandCount - Math.max(1, Math.round(bandCount * (rate ?? 0)));
  const tone = unsafe ? "hit" : "warn";

  return (
    <svg
      className="elev"
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={`${floors} floors, ${pct(rate)} of units in distress`}
    >
      {Array.from({ length: bandCount }, (_, band) => (
        <rect
          key={band}
          className={`band ${band >= litFrom ? tone : ""}`}
          x="1"
          y={2 + band * (bandHeight + GAP)}
          width={WIDTH - 2}
          height={bandHeight}
          style={{ animationDelay: `${index * 18 + (bandCount - band) * 14}ms` }}
        />
      ))}
      <line className="ground" x1="0" y1={HEIGHT - 1} x2={WIDTH} y2={HEIGHT - 1} />
    </svg>
  );
}
